import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import {
  X,
  Home,
  Users,
  Building2,
  Briefcase,
  Search,
  BarChart3,
  Settings,
  FileText,
  Calendar,
  MessageSquare,
  CreditCard,
  Shield,
  Zap,
  Clock,
  CheckCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import benchLogo from '../../assets/Bench_logo.png'

const Sidebar = ({ isOpen, onClose, isCollapsed, toggleCollapse }) => {
  const location = useLocation()
  const { user } = useAuth()
  const [isDesktop, setIsDesktop] = useState(false)

  // Get vendor status for conditional rendering
  let vendorStatus = null
  try {
    const vendorStatusContext = useVendorStatus()
    vendorStatus = vendorStatusContext
  } catch (error) {
    // VendorStatusContext not available, use default values
    vendorStatus = {
      isVendorActive: false,
      isVendorPending: true,
      documentCount: 0,
      resourceCount: 0
    }
  }

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const update = () => setIsDesktop(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  const getNavigationItems = () => {
    const baseItems = [
      { name: 'Dashboard', href: '/dashboard', icon: Home },
    ]

    if (user?.role === 'vendor') {
      const vendorItems = [
        ...baseItems,
        { name: 'Profile', href: '/dashboard/vendor/profile', icon: Building2 },
        { name: 'Compliance', href: '/dashboard/vendor/compliance', icon: Shield },
      ]

      // Always visible tabs with conditional content
      vendorItems.push(
        {
          name: 'Resources',
          href: '/dashboard/vendor/resources',
          icon: Users,
          badge: vendorStatus.resourceCount > 0 ? vendorStatus.resourceCount : null
        },
        {
          name: 'Subscription',
          href: '/dashboard/vendor/subscription',
          icon: CreditCard
        },
        {
          name: 'Payments',
          href: '/dashboard/vendor/payments',
          icon: CreditCard
        }
      )

      // Status-dependent items (only for active vendors)
      if (vendorStatus.isVendorActive) {
        vendorItems.push(
          { name: 'Analytics', href: '/dashboard/vendor/analytics', icon: BarChart3 },
          { name: 'Contracts', href: '/dashboard/vendor/contracts', icon: FileText },
          { name: 'Sub-accounts', href: '/dashboard/vendor/sub-accounts', icon: Users }
        )
      }

      return vendorItems
    } else if (user?.role === 'company') {
      return [
        ...baseItems,
        // { name: 'Marketplace', href: '/marketplace', icon: Search },
        { name: 'Talent Search', href: '/dashboard/company/talent', icon: Search },
        { name: 'Received', href: '/dashboard/company/jobs', icon: FileText },
        { name: 'Shortlists', href: '/dashboard/company/shortlists', icon: Users },
        { name: 'Interviews', href: '/dashboard/company/interviews', icon: Calendar },
        { name: 'Analytics', href: '/dashboard/company/analytics', icon: BarChart3 },
        { name: 'Compliance', href: '/dashboard/company/compliance', icon: Shield },
        { name: 'Reports', href: '/dashboard/company/reports', icon: FileText },
        { name: 'Settings', href: '/dashboard/company/settings', icon: Settings },
      ]
    } else if (user?.role === 'freelancer') {
      return [
        ...baseItems,
        { name: 'Marketplace', href: '/marketplace', icon: Search },
        { name: 'Job Board', href: '/dashboard/freelancer/jobs', icon: Briefcase },
        { name: 'Proposals', href: '/dashboard/freelancer/proposals', icon: FileText },
        { name: 'Messages', href: '/dashboard/freelancer/messages', icon: MessageSquare },
        { name: 'Workspace', href: '/dashboard/freelancer/workspace', icon: FileText },
        { name: 'Earnings', href: '/dashboard/freelancer/earnings', icon: CreditCard },
        { name: 'Reputation', href: '/dashboard/freelancer/reputation', icon: BarChart3 },
        { name: 'Profile', href: '/dashboard/freelancer/profile', icon: Users },
      ]
    }

    return baseItems
  }

  const navigationItems = getNavigationItems()

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    if (href === '/dashboard') {
      // Dashboard should only be active if it's exactly /dashboard or /dashboard/ (with redirect)
      return location.pathname === '/dashboard' || location.pathname === '/dashboard/'
    }
    return location.pathname.startsWith(href)
  }

  return (
    <>
      {/* Mobile overlay */}
      {!isDesktop && isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <motion.div
        initial={{ x: -300 }}
        animate={{
          x: isDesktop ? 0 : (isOpen ? 0 : -300),
          width: isDesktop && isCollapsed ? 80 : 256
        }}
        transition={{ duration: 0.3 }}
        className={`fixed inset-y-0 left-0 z-50 bg-white border-r border-secondary-200 dark:bg-secondary-900 dark:border-secondary-800 ${isDesktop ? '' : (isOpen ? 'translate-x-0' : '-translate-x-full')
          }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className={`flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-800 ${isCollapsed && isDesktop ? 'px-4 justify-center' : ''}`}>
            {!isCollapsed && (
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 flex-shrink-0">
                  <img
                    src={benchLogo}
                    alt="AJA Bench Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="text-xl font-bold gradient-text">AJA Bench</span>
              </div>
            )}

            {/* Logo when collapsed */}
            {isCollapsed && isDesktop && (
              <div className="w-10 h-10 flex-shrink-0 mb-2">
                <img
                  src={benchLogo}
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            )}

            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg text-secondary-600 hover:bg-secondary-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Desktop Toggle Button */}
            {isDesktop && !isCollapsed && (
              <button
                onClick={toggleCollapse}
                className="hidden lg:flex p-1 rounded-md text-secondary-400 hover:text-secondary-600 hover:bg-secondary-50 absolute right-4"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Collapsed Header Toggle (Centered) */}
          {isDesktop && isCollapsed && (
            <div className="flex justify-center py-2 border-b border-secondary-100 dark:border-secondary-800">
              <button
                onClick={toggleCollapse}
                className="p-1 rounded-md text-secondary-400 hover:text-secondary-600 hover:bg-secondary-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon
              const isDisabled = item.disabled
              const hasBadge = item.badge !== null && item.badge !== undefined

              const linkContent = (
                <div className={`flex items-center ${isCollapsed && isDesktop ? 'justify-center' : 'justify-between'} w-full ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <div className={`flex items-center ${isCollapsed && isDesktop ? '' : 'space-x-3'}`}>
                    <Icon className="w-5 h-5" title={isCollapsed ? item.name : ''} />
                    {!isCollapsed && <span>{item.name}</span>}
                  </div>
                  {!isCollapsed && hasBadge && (
                    <span className="bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 text-xs px-2 py-1 rounded-full">
                      {item.badge}
                    </span>
                  )}
                  {!isCollapsed && isDisabled && (
                    <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  )}
                  {isCollapsed && hasBadge && (
                    <div className="absolute top-1 right-1 w-2 h-2 bg-primary-600 rounded-full" />
                  )}
                </div>
              )

              if (isDisabled) {
                return (
                  <div
                    key={item.name}
                    title={item.tooltip || item.name}
                    className={`flex items-center ${isCollapsed && isDesktop ? 'justify-center px-2' : 'px-3 space-x-3'} py-2 rounded-lg text-sm font-medium text-secondary-400 dark:text-secondary-500 cursor-not-allowed relative`}
                  >
                    {linkContent}
                  </div>
                )
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={!isDesktop ? onClose : undefined}
                  className={`flex items-center ${isCollapsed && isDesktop ? 'justify-center px-2' : 'px-3 space-x-3'} py-2 rounded-lg text-sm font-medium transition-colors relative group ${isActive(item.href)
                    ? 'bg-primary-100 text-primary-700 dark:bg-secondary-800 dark:text-primary-300'
                    : 'text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900 dark:text-secondary-200 dark:hover:bg-secondary-800 dark:hover:text-white'
                    }`}
                  title={isCollapsed && isDesktop ? item.name : ''}
                >
                  {linkContent}
                </Link>
              )
            })}
          </nav>
        </div>
      </motion.div>
    </>
  )
}

export default Sidebar
